import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../context/auth";
import { maskMoney } from "../../../utils/mask";
import {
  IBuildingOption,
  IConstructor,
  IImovel,
  IProposta,
} from "../types/proposta";
import { moneyToNumber } from "../utils/propostaMappers";
import {
  formatBuildingLabel,
  formatBuildingLabelFromApi,
} from "../utils/formatBuildingLabel";

const isCadastroNovoId = (id?: string | number | null) => Number(id) === -1;

function normalizeBuildingList(data: unknown): IBuildingOption[] {
  if (!data) return [];
  const list = Array.isArray(data) ? data : [];
  return list.map(
    (item: {
      id: number;
      id_display?: number;
      descricao?: string;
      titulo?: string;
      id_imovel_kurole?: string;
    }) => ({
      id: item.id,
      id_display: item.id_display ?? item.id,
      descricao: item.descricao || item.titulo || `Imóvel #${item.id}`,
      id_imovel_kurole: item.id_imovel_kurole,
    }),
  );
}

export function usePropostaImovel(proposta: IProposta) {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [buildings, setBuildings] = useState<IBuildingOption[]>([]);
  const [constructors, setConstructors] = useState<IConstructor[]>([]);
  const [searchQuery, setSearchQuery] = useState(
    proposta.imovel?.descricao_imovel || "",
  );
  const [showBuildingList, setShowBuildingList] = useState(false);

  const [idImovel, setIdImovelState] = useState(
    proposta.imovel?.id_imovel || "",
  );
  const [descricaoImovel, setDescricaoImovel] = useState(
    proposta.imovel?.descricao_imovel || "",
  );
  const [idConstrutora, setIdConstrutora] = useState(
    proposta.imovel?.id_construtora || "",
  );
  const [descricaoConstrutora, setDescricaoConstrutora] = useState(
    proposta.imovel?.descricao_construtora || "",
  );
  const [unidade, setUnidade] = useState(proposta.imovel?.unidade || "");
  const [bloco, setBloco] = useState(proposta.imovel?.bloco || "");
  const [valorImovel, setValorImovel] = useState<number | "">(() => {
    const raw = proposta.imovel?.valor;
    if (!raw) return "";
    const num = moneyToNumber(raw);
    return num > 0 ? num : "";
  });
  const [valorProposta, setValorProposta] = useState(
    proposta.imovel?.valor || "",
  );
  const [idImovelKurole, setIdImovelKurole] = useState(
    proposta.imovel?.id_imovel_kurole || "",
  );
  const [cep, setCep] = useState(proposta.imovel?.endereco?.cep || "");
  const [logradouro, setLogradouro] = useState(
    proposta.imovel?.endereco?.logradouro || "",
  );
  const [numero, setNumero] = useState(proposta.imovel?.endereco?.numero || "");
  const [bairro, setBairro] = useState(proposta.imovel?.endereco?.bairro || "");
  const [cidade, setCidade] = useState(proposta.imovel?.endereco?.cidade || "");
  const [estado, setEstado] = useState(proposta.imovel?.endereco?.estado || "");
  const [uf, setUf] = useState(
    proposta.imovel?.endereco?.uf || proposta.imovel?.endereco?.estado || "",
  );
  const [isSemiNovo, setIsSemiNovo] = useState(
    proposta.imovel?.imovel_seminovo ?? 0,
  );

  const idImovelRef = useRef(idImovel);
  idImovelRef.current = idImovel;

  const isImovelNovo = isCadastroNovoId(idImovel);
  const mostrarEndereco = false;
  // Fonte de verdade: App HUB — trava cadastro quando a proposta já tem id.
  const lockImovelEdit = Boolean(proposta?.id_proposta);

  const fetchConstructors = useCallback(async () => {
    try {
      const res = await api.get("/portal/constructors?portal=hub");
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setConstructors(
        data
          .map((c: { id: number; nome: string }) => ({
            id: c.id,
            nome: c.nome,
          }))
          .sort((a: IConstructor, b: IConstructor) =>
            a.nome.localeCompare(b.nome),
          ),
      );
    } catch (error) {
      console.error("Erro ao buscar construtoras:", error);
    }
  }, [api]);

  const searchBuildings = useCallback(async (query: string) => {
    setLoading(true);
    try {
      const encoded = encodeURIComponent(query || "");
      const res = await api.get(
        `/portal/building/list-building/0?query_search=${encoded}&portal=hub`,
      );
      let items = normalizeBuildingList(res.data);
      if (query) {
        items = [
          { id: -1, id_display: -1, descricao: query, id_imovel_kurole: undefined },
          ...items,
        ];
      }
      setBuildings(items);
    } catch (error) {
      console.error("Erro ao buscar imóveis:", error);
      setBuildings([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchImovelData = useCallback(
    async (fetchId: string) => {
      if (isCadastroNovoId(fetchId) || !fetchId) return;
      setLoading(true);
      try {
        const corretorParam = user?.id_corretor ? `&id_corretor=${user.id_corretor}&seller=${user.id_corretor}` : "";
        const res = await api.get(
          `/portal/building/${fetchId}?portal=hub${corretorParam}`,
        );
        if (String(idImovelRef.current) !== fetchId) return;
        const data = Array.isArray(res.data) ? res.data[0] : res.data;
        if (!data) return;

        setIdImovelKurole(data.id_imovel_kurole ?? "");
        setIdConstrutora(String(data?.empreendimento?.id_construtora || ""));
        setDescricaoConstrutora(data?.empreendimento?.nome || "");

        const label = formatBuildingLabelFromApi(data);
        if (label) {
          setDescricaoImovel(label);
          setSearchQuery(label);
        }
        setUnidade(data.ap_numero ? String(data.ap_numero) : "N/A");
        setBloco(data.ap_bloco ? String(data.ap_bloco) : "N/A");

        const chosenValue = data.valor_venda ?? data.valor_aluguel ?? "";
        if (chosenValue !== "" && chosenValue != null) {
          const num = Number(chosenValue);
          setValorImovel(num);
          setValorProposta(
            maskMoney(String(Math.round(num * 100))),
          );
        } else {
          setValorImovel("");
        }
      } catch (error) {
        console.error("Erro ao buscar imóvel:", error);
      } finally {
        if (String(idImovelRef.current) === fetchId) {
          setLoading(false);
        }
      }
    },
    [api, user?.id_corretor],
  );

  const selectBuilding = useCallback(
    (building: IBuildingOption) => {
      if (building.id === -1) {
        setIdImovelState("-1");
        const label = searchQuery || building.descricao;
        setDescricaoImovel(label);
        setSearchQuery(label);
        setValorImovel("");
        setShowBuildingList(false);
        return;
      }

      const label = formatBuildingLabel(building);

      setIdImovelState(String(building.id));
      setDescricaoImovel(label);
      setShowBuildingList(false);
      setSearchQuery(label);
      fetchImovelData(String(building.id));
    },
    [searchQuery, fetchImovelData],
  );

  const variationInfo = useMemo(() => {
    if (
      valorImovel === "" ||
      valorProposta === "" ||
      valorProposta === null ||
      valorProposta === undefined
    ) {
      return { diffValue: 0, percent: 0 };
    }

    const base = typeof valorImovel === "number" ? valorImovel : 0;
    const propostaNum = moneyToNumber(valorProposta);

    if (base === 0 || isNaN(base) || isNaN(propostaNum)) {
      return { diffValue: 0, percent: 0 };
    }

    const diff = propostaNum - base;
    const percent = (diff / base) * 100;
    return { diffValue: diff, percent };
  }, [valorImovel, valorProposta]);

  useEffect(() => {
    fetchConstructors();
    searchBuildings("");
  }, [fetchConstructors, searchBuildings]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchBuildings(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchBuildings]);

  useEffect(() => {
    const cepDigits = cep.replace(/\D/g, "");
    if (cepDigits.length !== 8 || !isImovelNovo) return;
    const fetchCep = async () => {
      try {
        const response = await fetch(
          `https://viacep.com.br/ws/${cepDigits}/json/`,
        );
        const data = await response.json();
        if (data?.erro) return;
        setLogradouro(data.logradouro ?? "");
        setBairro(data.bairro ?? "");
        setCidade(data.localidade ?? "");
        setEstado(data.uf ?? "");
        setUf(data.uf ?? "");
      } catch {
        // ignore
      }
    };
    fetchCep();
  }, [cep, isImovelNovo]);

  const buildImovelToSave = useCallback((): IImovel => {
    return {
      id_imovel: idImovel,
      descricao_imovel: descricaoImovel,
      bloco,
      imovel_seminovo: isSemiNovo,
      unidade,
      id_construtora: idConstrutora,
      descricao_construtora: descricaoConstrutora,
      valor: valorProposta,
      ...(isImovelNovo
        ? {
            endereco: {
              cep,
              logradouro,
              numero,
              bairro,
              cidade,
              estado,
              uf: uf || estado,
            },
          }
        : {}),
      ...(isImovelNovo ? {} : { id_imovel_kurole: idImovelKurole }),
    };
  }, [
    idImovel,
    descricaoImovel,
    bloco,
    isSemiNovo,
    unidade,
    idConstrutora,
    descricaoConstrutora,
    valorProposta,
    isImovelNovo,
    cep,
    logradouro,
    numero,
    bairro,
    cidade,
    estado,
    uf,
    idImovelKurole,
  ]);

  return {
    loading,
    lockImovelEdit,
    isImovelNovo,
    mostrarEndereco,
    buildings,
    constructors,
    searchQuery,
    setSearchQuery,
    showBuildingList,
    setShowBuildingList,
    selectBuilding,
    idImovel,
    descricaoImovel,
    setDescricaoImovel,
    idConstrutora,
    setIdConstrutora,
    descricaoConstrutora,
    setDescricaoConstrutora,
    unidade,
    setUnidade,
    bloco,
    setBloco,
    valorImovel,
    valorProposta,
    setValorProposta,
    idImovelKurole,
    variationInfo,
    cep,
    setCep,
    logradouro,
    setLogradouro,
    numero,
    setNumero,
    bairro,
    setBairro,
    cidade,
    setCidade,
    estado,
    setEstado,
    uf,
    setUf,
    isSemiNovo,
    setIsSemiNovo,
    buildImovelToSave,
  };
}
